import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The reasoning half of Geometry.
 *
 * These lessons carry the shift the whole course exists for: from
 * measuring figures to arguing about them. Everything after depends on a
 * student accepting that a drawn example proves nothing, which is a genuinely
 * difficult idea and is treated as one here rather than asserted in passing.
 */
export const GEOMETRY_PROOF_LESSONS: Lesson[] = [
  lesson({
    id: 'geometric-proof-01',
    unitId: 'unit-proof',
    conceptId: 'geometric-proof',
    title: 'Reasoning and Proof',
    summary: 'Arguing from what is given to what must follow — the move that separates mathematics from measurement.',
    objectives: [
      'Distinguish a conjecture from a proof',
      'Tell a statement from its converse',
      'Write a short chain of reasoning with a justification at every step',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Why measuring is not enough',
        blocks: [
          text('Draw a triangle. Measure its three angles and add them: 180°. Draw another, and another. Always 180°.'),
          text('How many would you need before you were sure it happens for *every* triangle — including the ones with a side a thousand miles long, and the ones nobody will ever draw?'),
          insight('The answer is that no number of measurements is enough. Measurement can tell you a thing is *usually* so; only an argument can tell you it *must* be so. Producing such arguments is what this course is about.'),
        ],
      },
      intuition: {
        title: 'What a proof is made of',
        blocks: [
          text('A proof is a chain. Each link is a claim, and each claim carries a reason you are entitled to use — something already established, not something the picture suggests.'),
          list([
            '**Definitions** — agreed meanings. A right angle *is* 90°; nothing is being claimed.',
            '**Postulates** — starting points accepted without proof, because an argument has to begin somewhere.',
            '**Theorems** — results already proved, which you may now use freely.',
          ]),
          text('Written out, a proof looks like a two-column list: what you claim on the left, why you may claim it on the right.'),
          table(['Statement', 'Reason'], [
            ['∠ABC and ∠CBD are on a straight line', 'given'],
            ['∠ABC + ∠CBD = 180°', 'angles on a straight line'],
            ['∠ABC = 3x, ∠CBD = 2x', 'given'],
            ['5x = 180', 'substitution'],
            ['x = 36', 'dividing both sides by 5'],
          ]),
          insight('The right-hand column is the proof. Anyone can write the left-hand column by looking at a diagram; the discipline is being unable to write a line until you can say why.'),
          text('One distinction causes more trouble than any other. A statement and its **converse** are different claims, and the truth of one says nothing about the truth of the other.'),
          math('\\text{If } A \\text{ then } B \\qquad\\ne\\qquad \\text{If } B \\text{ then } A'),
          warning('Every square is a rectangle. Not every rectangle is a square. Both directions have to be argued separately, and assuming otherwise is the commonest error in this whole course.', 'A statement is not its converse'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Here is a conjecture worth being suspicious of. Take a circle, mark points on it, and join every pair. Count the regions.'),
          table(['Points', 'Regions'], [
            ['1', '1'],
            ['2', '2'],
            ['3', '4'],
            ['4', '8'],
            ['5', '16'],
            ['6', '**31**'],
          ]),
          insight('Five cases in perfect agreement with "it doubles", and then it does not. Nothing about the first five was wrong — they simply were not a proof, and there was no way to tell from inside the pattern.'),
          tip('This is why "I checked several examples" is not an argument. It is a good way to *find* something worth proving, and no way at all to establish it.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Two lines cross. Prove that the vertically opposite angles are equal.'),
          steps([
            { label: 'Name the angles: a and b are on one straight line.', latex: 'a + b = 180°' },
            { label: 'b and c are also on a straight line.', latex: 'b + c = 180°' },
            { label: 'Both totals are 180, so they are equal to each other.', latex: 'a + b = b + c' },
            { label: 'Subtract b from both sides.', latex: 'a = c' },
          ]),
          text('Nothing was measured, and no particular pair of lines was used. The argument holds for every pair of crossing lines there could be — which is what makes it a theorem rather than an observation.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('geometric-proof', 4)] },
      independent: { title: 'On your own', blocks: [practice('geometric-proof', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('geometric-proof', 3, 'Converses, and what does and does not justify a step.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Measuring examples suggests; only an argument establishes.',
            'Every step of a proof needs a reason: a definition, a postulate or a theorem.',
            'A statement and its converse are different claims.',
            'A pattern holding for five cases can fail at the sixth.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'conditional-statements-01',
    unitId: 'unit-proof',
    conceptId: 'conditional-statements',
    title: 'Conditional Statements',
    summary: 'If A then B — and the three other statements you can build from it, only one of which comes free.',
    objectives: [
      'Name the hypothesis and the conclusion of a conditional',
      'Write the converse, inverse and contrapositive of a statement',
      'Refute a universal claim with a single counterexample',
      'Tell inductive reasoning from deductive reasoning',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'The shape almost every theorem has',
        blocks: [
          text('Look at what you have proved so far and every one of them has the same form: *if* something is the case, *then* something else follows.'),
          math('\\text{If } A \\text{ then } B'),
          text('A sentence of this shape is a **conditional**. The part after "if" is the **hypothesis**, the part after "then" is the **conclusion**, and the statement claims nothing whatever about a figure that fails the hypothesis.'),
          insight('That last point does most of the damage in this topic. "If a quadrilateral is a square then it has four right angles" says nothing at all about a shape that is not a square. It has not been ruled out; it has simply not been mentioned.'),
        ],
      },
      intuition: {
        title: 'Three ways to rearrange one sentence',
        blocks: [
          text('Given a conditional, there are three standard things to do to it, and it is worth being able to write all three quickly.'),
          table(['Name', 'Form', 'Made by'], [
            ['Conditional', 'if A then B', '—'],
            ['Converse', 'if B then A', 'swapping'],
            ['Inverse', 'if not A then not B', 'negating'],
            ['Contrapositive', 'if not B then not A', 'swapping *and* negating'],
          ]),
          text('Now the fact the whole topic turns on. Of those three, exactly one is guaranteed to be true whenever the original is.'),
          insight('The **contrapositive** is always as true as the statement it came from. The converse and the inverse are not — and they stand or fall together, because each is the contrapositive of the other.'),
          text('Try it on something concrete. Take: if a number is a multiple of 4, then it is even.'),
          list([
            '**Converse** — if a number is even then it is a multiple of 4. **False**: 6 is even.',
            '**Inverse** — if a number is not a multiple of 4 then it is not even. **False**: 6 again.',
            '**Contrapositive** — if a number is not even then it is not a multiple of 4. **True**, and it has to be.',
          ]),
          warning('The contrapositive is not the converse with minus signs on it. Reversing alone gives the converse; reversing *and* negating gives the contrapositive. Only the second one is safe to use in a proof.', 'Both moves, or neither'),
          text('When a statement and its converse are both true, the two are joined into one **biconditional**, written "A if and only if B" — and that is what a definition is. A shape is a rhombus if and only if it is a quadrilateral with four equal sides: the claim runs in both directions, which is what entitles you to use it in either.'),
          text('One more distinction. Checking cases and spotting a pattern is **inductive** reasoning: it is how conjectures are found, and it can be wrong. Starting from statements already accepted and forcing a conclusion is **deductive** reasoning: it is what a proof is made of, and it cannot be.'),
          insight('The asymmetry is sharp, and in your favour. To *establish* "every A is a B" you need an argument covering every case. To *destroy* it you need one A that is not a B — a **counterexample** — and a second one adds nothing, because the claim was already dead.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Draw the As inside the Bs. That single picture answers all four questions at once.'),
          table(['Statement', 'Reads as', 'True?'], [
            ['If A then B', 'every A is inside B', 'yes — that is the picture'],
            ['If B then A', 'every B is inside A', 'no — B is the bigger region'],
            ['If not A then not B', 'outside A is outside B', 'no — the ring between them'],
            ['If not B then not A', 'outside B is outside A', 'yes — nothing outside B can be inside A'],
          ]),
          tip('The ring between the two circles is where the converse and the inverse both fail. Anything living there is a B but not an A — and it is exactly the counterexample you were looking for.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Take: if two angles are vertically opposite, then they are equal. Write its converse, inverse and contrapositive, and decide which are true.'),
          steps([
            { label: 'Name the two halves.', latex: 'A: \\text{vertically opposite} \\qquad B: \\text{equal}' },
            { label: 'Converse — swap. False: two 40° angles drawn anywhere at all are equal without being vertically opposite, and one counterexample settles it.', latex: '\\text{if equal, then vertically opposite}' },
            { label: 'Inverse — negate both. False, and the same counterexample does it, as it must: the inverse and the converse rise and fall together.', latex: '\\text{if not vertically opposite, then not equal}' },
            { label: 'Contrapositive — swap and negate. True, and no separate argument is needed: it says what the original said.', latex: '\\text{if not equal, then not vertically opposite}' },
          ]),
          text('So one theorem gave four sentences, two of them false, and only one of the three rearrangements could be used without proving anything further.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('conditional-statements', 4)] },
      independent: { title: 'On your own', blocks: [practice('conditional-statements', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('conditional-statements', 3, 'Contrapositives, counterexamples, and which rearrangement you are entitled to use.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A conditional is "if hypothesis then conclusion", and it says nothing about figures failing the hypothesis.',
            'Converse: swap. Inverse: negate. Contrapositive: swap and negate.',
            'The contrapositive is always as true as the original; the converse and inverse are not, and they stand or fall together.',
            'A biconditional — "if and only if" — is a statement and its converse both, which is what a definition is.',
            'Inductive reasoning suggests; deductive reasoning establishes.',
            'One counterexample refutes a claim about every case. A second one adds nothing.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'parallel-lines-01',
    unitId: 'unit-proof',
    conceptId: 'parallel-lines',
    title: 'Parallel Lines and Transversals',
    summary: 'One line crossing two parallels makes eight angles, and only two different sizes among them.',
    objectives: [
      'Name corresponding, alternate and co-interior angle pairs',
      'Find unknown angles using those relationships',
      'Use the converse to prove two lines are parallel',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Eight angles, two sizes',
        blocks: [
          text('A **transversal** is a line crossing two others. When the two are parallel, something tidy happens: the eight angles produced take only two different values, and those two add to 180°.'),
          text('So knowing one of the eight gives you all eight. The work is in knowing which are equal and which are supplementary.'),
        ],
      },
      intuition: {
        title: 'Position decides the relationship',
        blocks: [
          text('Each pair has a name, and each name describes where the two angles sit.'),
          table(['Pair', 'Where they sit', 'Relationship'], [
            ['Corresponding', 'the same corner at each crossing', 'equal'],
            ['Alternate', 'between the parallels, opposite sides of the transversal', 'equal'],
            ['Co-interior', 'between the parallels, the same side of the transversal', 'add to 180°'],
            ['Vertically opposite', 'facing each other at one crossing', 'equal'],
          ]),
          insight('Corresponding angles are the easiest to see: slide one crossing along the transversal onto the other, and the two figures land exactly on top of each other, because the lines are parallel. Everything else follows from that plus angles on a straight line.'),
          warning('Co-interior angles are the ones that catch people, because they *look* like the others and behave differently. They are on the same side, so together they make a straight line: supplementary, not equal.', 'The pair that differs'),
          text('And these relationships run both ways, which is more useful than it first appears. If a pair of alternate angles is equal, the lines *must* be parallel.'),
          insight('That converse is how parallelism gets proved rather than assumed. It is also a rare case where the converse genuinely does hold — which is exactly why it has to be stated and proved separately rather than taken for granted.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'parallel-lines', mark: [1, 5] },
            'Angles 1 and 5 are corresponding — the same corner at each crossing — so they are equal.',
          ),
          figure(
            { kind: 'parallel-lines', mark: [4, 6] },
            'Angles 4 and 6 are alternate: between the parallels, on opposite sides of the transversal. Also equal.',
          ),
          figure(
            { kind: 'parallel-lines', mark: [3, 6] },
            'Angles 3 and 6 are co-interior: between the parallels on the same side. These add to 180°.',
          ),
          tip('If the names will not stick, fall back on the picture: all the acute angles are equal to each other, all the obtuse ones are equal to each other, and one of each adds to 180°. That is enough to answer most questions.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A transversal crosses two parallel lines. One angle is 118°. Find the co-interior angle, and the alternate angle.'),
          steps([
            { label: 'Co-interior angles are supplementary.', latex: '180 - 118 = 62°' },
            { label: 'Alternate angles are equal.', latex: '118°' },
            { label: 'Check: the two values are 118 and 62.', latex: '118 + 62 = 180 \\;\\checkmark' },
          ]),
          text('Only two distinct sizes appear among all eight angles, and they are supplementary. That check catches almost any slip.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('parallel-lines', 4)] },
      independent: { title: 'On your own', blocks: [practice('parallel-lines', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('parallel-lines', 3, 'Naming the pair, not only finding the number.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Corresponding and alternate angles are equal.',
            'Co-interior angles add to 180°.',
            'Only two different sizes appear among the eight, and they are supplementary.',
            'The converses hold: equal alternate angles prove the lines are parallel.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'triangle-congruence-01',
    unitId: 'unit-triangles',
    conceptId: 'triangle-congruence',
    title: 'Congruent Triangles',
    summary: 'Three of the six measurements are enough to fix a triangle completely — but only the right three.',
    objectives: [
      'State the congruence criteria SSS, SAS, ASA, AAS and HL',
      'Explain why SSA and AAA are not among them',
      'Use congruence to justify that two parts are equal',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'How much do you need to know?',
        blocks: [
          text('A triangle has six measurements: three sides and three angles. Two triangles are **congruent** when all six match — same shape, same size, one would lie exactly on the other.'),
          text('Checking all six would be tedious, and the good news is that you never have to. Three of them, chosen properly, force the other three.'),
          insight('That is a genuinely surprising claim. It says the six measurements are not independent: fix the right three and the triangle has no freedom left.'),
        ],
      },
      intuition: {
        title: 'Which three, and why not the others',
        blocks: [
          text('Four combinations work, plus one special case for right triangles.'),
          table(['Criterion', 'What matches', 'Why it works'], [
            ['SSS', 'three sides', 'the sides can only meet one way'],
            ['SAS', 'two sides and the angle between', 'the third side is forced'],
            ['ASA', 'two angles and the side between', 'the arms meet at one point'],
            ['AAS', 'two angles and a side not between', 'the third angle is forced, giving ASA'],
            ['HL', 'hypotenuse and a leg, right triangles', 'the other leg follows from Pythagoras'],
          ]),
          text('Two combinations look plausible and are not criteria at all, and knowing *why* matters more than memorising the list.'),
          warning('**AAA** fixes the shape but not the size. A photograph and its enlargement have identical angles and different measurements — that is similarity, not congruence.', 'Angles alone'),
          warning('**SSA** genuinely fails. With two sides and an angle *not* between them, the second side can often swing to meet the base in two different places, giving two different triangles from the same three numbers. The angle has to sit between the two sides.', 'The ambiguous case'),
          insight('There is a cleaner way to say all of this, and it comes from Grade 8. Two figures are congruent when some sequence of rigid motions — slides, turns, flips — carries one exactly onto the other. The criteria are just convenient tests for when such a motion exists.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'b', height: 'a', hypotenuse: 'c' } },
            'Fix a, b and the angle between them, and c has no choice. That is SAS.',
          ),
          text('The ambiguous case is worth picturing. Given a base, an angle at one end, and the length of the far side, that far side is an arc swung from a point — and an arc can cross the base twice.'),
          tip('Write the vertices in matching order when you state a congruence: △ABC ≅ △DEF means A corresponds to D, B to E, C to F. Everything you then deduce depends on that order being right.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('In triangles ABC and DEF: AB = DE, ∠A = ∠D, and AC = DF. Are they congruent, and does BC = EF follow?'),
          steps([
            { label: 'Two sides are given.', latex: 'AB = DE,\\quad AC = DF' },
            { label: 'The angle given is at A, between those two sides.', latex: '\\angle A = \\angle D' },
            { label: 'So this is SAS.', latex: '\\triangle ABC \\cong \\triangle DEF' },
            { label: 'Corresponding parts of congruent triangles are equal.', latex: 'BC = EF' },
          ]),
          text('The last step has a name worth having: once congruence is established, *every* corresponding pair matches, and you may use any of them. Establishing congruence is usually the means, not the end.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('triangle-congruence', 4)] },
      independent: { title: 'On your own', blocks: [practice('triangle-congruence', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('triangle-congruence', 3, 'Telling a real criterion from a plausible one.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Congruent means all six measurements match.',
            'SSS, SAS, ASA, AAS and HL are enough; each forces the remaining three.',
            'AAA gives similarity, not congruence.',
            'SSA can describe two different triangles, so it proves nothing.',
            'Once congruent, every corresponding pair is equal.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'constructions-01',
    unitId: 'unit-triangles',
    conceptId: 'constructions',
    title: 'Constructions with Compass and Straightedge',
    summary: 'Drawing a figure that is exactly right rather than nearly right — and proving it with the congruence you already have.',
    objectives: [
      'Construct a perpendicular bisector and an angle bisector',
      'Explain why each construction is exact, using triangle congruence',
      'Recognise a bisector as a set of equidistant points',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two tools, and one of them cannot measure',
        blocks: [
          text('A straightedge draws a line through two points. A compass draws a circle. Neither has a number on it, and that is the point of the exercise rather than a limitation of it.'),
          text('A ruler-and-protractor drawing of the midpoint of a segment is accurate to about half a millimetre. A constructed midpoint is not accurate to anything — it is *the* midpoint, and there is a proof that it is.'),
          insight('This is the same distinction the course opened with. Measuring tells you about the figure in front of you; a construction, with its argument, tells you about every figure of that kind. The compass is a proof written in ink.'),
        ],
      },
      intuition: {
        title: 'Why a compass is an argument',
        blocks: [
          text('A compass has exactly one property worth having: while the opening is unchanged, every point it draws is the **same distance** from where the point stood. That single fact is what every construction below is made of.'),
          text('**The perpendicular bisector of AB.** Open the compass wider than half of AB. Swing an arc from A, and — without changing the opening — an arc from B. Call the crossings P and Q, and draw PQ.'),
          list([
            'PA = PB and QA = QB, because the opening never changed between the two arcs.',
            'Triangles APQ and BPQ share the side PQ, so SSS makes them congruent — and the angles at P correspond: ∠APM = ∠BPM, writing M for the point where PQ crosses AB.',
            'Now compare triangles APM and BPM: PA = PB, the angles at P are the ones just shown equal, and PM is shared. That is SAS, so AM = BM and ∠AMP = ∠BMP.',
            'Those last two angles sit together on the straight line AB. Equal and adding to 180°, they are 90° each — so PQ cuts AB in half and at right angles.',
          ], true),
          warning('The opening must exceed half of AB. Narrower and the two arcs never meet; exactly half and they touch at a single point, which gives nothing to draw a line through. Any wider opening works, which is precisely why no measuring is involved.', 'The one condition'),
          text('**The bisector of an angle.** Put the compass on the vertex and cut both arms at P and Q. Swing equal arcs from P and from Q; join the vertex to where they meet. The same SSS argument makes the two halves congruent.'),
          insight('Both constructions are really statements about *distance*. Every point equidistant from A and B lies on the perpendicular bisector of AB, and every point equidistant from two lines lies on the bisector of the angle between them. Read that way, the construction is not a recipe but a description of a set of points.'),
          tip('That reading answers a harder question for free. The three perpendicular bisectors of a triangle meet at one point — the circumcentre — because a point on two of them is equidistant from all three vertices, and so must lie on the third as well.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'construction', construction: 'perpendicular-bisector' },
            'Two arcs of one opening, swung from each end. The line through their crossings is perpendicular to AB and cuts it in half.',
          ),
          figure(
            { kind: 'construction', construction: 'angle-bisector' },
            'An arc cuts both arms; two more equal arcs meet on the bisector. The dashed curves are the argument, not decoration.',
          ),
          figure(
            { kind: 'construction', construction: 'equilateral-triangle' },
            'The first proposition of Euclid: two arcs the length of AB give a triangle whose three sides are radii of equal circles.',
          ),
          table(['Construction', 'What the compass fixes', 'What proves it'], [
            ['Perpendicular bisector', 'PA = PB and QA = QB', 'SSS, then equal angles on a line'],
            ['Angle bisector', 'Equal arcs on both arms', 'SSS on the two half-triangles'],
            ['Equilateral triangle', 'Both new sides equal AB', 'All three sides are radii of equal circles'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('An angle of 96° is bisected, and one of the halves is bisected again. What angles appear, and why is each exact?'),
          steps([
            { label: 'The first bisection.', latex: '96 \\div 2 = 48^\\circ \\text{ twice}' },
            { label: 'Bisecting one half.', latex: '48 \\div 2 = 24^\\circ \\text{ twice}' },
            { label: 'Check against the whole.', latex: '24 + 24 + 48 = 96 \\;\\checkmark' },
          ]),
          text('Each step is exact because congruent triangles force the two parts to be equal — not because the drawing looks even. Notice what is *not* available: 96° cannot be trisected this way, and no sequence of compass and straightedge steps will do it. Halving is what these two tools give you.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('constructions', 4)] },
      independent: { title: 'On your own', blocks: [practice('constructions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('constructions', 3, 'Why a construction is exact, and where the lines meet.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A compass makes equal lengths; congruence turns those into equal angles.',
            'Perpendicular bisector: equal arcs from both ends, opening wider than half.',
            'Angle bisector: an arc across both arms, then equal arcs from the two cuts.',
            'Equidistant from two points is the perpendicular bisector; from two lines, the angle bisector.',
          ]),
        ],
      },
    },
  }),
];
